import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError, formatCurrency, formatDate } from "@/lib/api";
import { EmptyState } from "@/components/app/ui-bits";
import { Plus, Wallet, FileText, Paperclip } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { useUpload } from "./_shared";
import RecordInspector, { useRecordInspector } from "@/components/app/RecordInspector";

export default function PettyCashTab({ canEdit }) {
  const inspector = useRecordInspector();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, s] = await Promise.all([
        api.get("/finance/petty-cash"),
        api.get("/finance/petty-cash/summary"),
      ]);
      setRows(r.data);
      setSummary(s.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const requestReplenishment = async () => {
    try {
      await api.post("/finance/petty-cash/request-replenishment");
      toast.success("Replenishment request sent");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const lowBalance = summary && summary.balance < (summary.warn_threshold || 50);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="dc-card p-4">
          <div className="overline">Current Balance</div>
          <div className={`mt-1 font-display text-2xl font-bold tabular-nums ${lowBalance ? "text-red-600" : "text-slate-900"}`}>
            {summary ? formatCurrency(summary.balance) : "—"}
          </div>
          <div className="text-xs text-slate-500">{summary?.account_label || "—"}</div>
        </div>
        <div className="dc-card p-4">
          <div className="overline">Float Limit</div>
          <div className="mt-1 font-display text-2xl font-bold tabular-nums text-slate-900">
            {summary ? formatCurrency(summary.float_limit) : "—"}
          </div>
        </div>
        <div className="dc-card p-4">
          <div className="overline">Last Replenishment</div>
          <div className="mt-1 font-display text-lg font-semibold text-slate-900">
            {summary?.last_replenishment_date ? formatDate(summary.last_replenishment_date) : "—"}
          </div>
        </div>
        <div className="dc-card p-4">
          <div className="overline">Custodian</div>
          <div className="mt-1 font-display text-lg font-semibold text-slate-900">{summary?.custodian || "—"}</div>
        </div>
      </div>

      {lowBalance && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="petty-cash-warning">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Wallet size={18} className="text-amber-700" />
              <div>
                <div className="font-medium text-amber-900">
                  Petty cash balance is below the ${summary.warn_threshold} threshold.
                </div>
                <div className="text-xs text-amber-800">Request replenishment so disbursements can continue.</div>
              </div>
            </div>
            <Button onClick={requestReplenishment} className="btn-primary-blue" data-testid="petty-cash-replenish-btn">
              Request Replenishment
            </Button>
          </div>
        </div>
      )}

      <div className="dc-card overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="font-display text-[15px] font-semibold text-slate-900">Transaction log</h3>
          {canEdit && (
            <Button onClick={() => setAddOpen(true)} className="btn-primary-blue" data-testid="petty-cash-add-btn">
              <Plus size={14} className="mr-1" /> New entry
            </Button>
          )}
        </div>
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <EmptyState icon={Wallet} title="No transactions" description="Record petty cash inflows and outflows here." />
        ) : (
          <div className="responsive-table-wrap">
            <table className="data-table w-full min-w-[1000px]">
              <thead>
                <tr>
                  <th>Date</th><th>Ref</th><th>Description</th><th>Category</th><th>Payee</th>
                  <th className="text-right text-emerald-700">In</th>
                  <th className="text-right text-red-700">Out</th>
                  <th className="text-right">Balance</th><th>Doc</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="text-sm cursor-pointer hover:bg-slate-50/70 transition-colors"
                    onClick={() => inspector.open(r, {
                      title: `Petty cash · ${r.ref_no || r.voucher_no}`,
                      subtitle: `${r.category || ""} · ${r.description || ""}`,
                    })}
                    data-testid={`pc-row-${r.ref_no}`}
                  >
                    <td className="px-5 py-3 font-mono-alt text-xs tabular-nums">{formatDate(r.date)}</td>
                    <td className="px-5 py-3 font-mono-alt text-xs">{r.ref_no}</td>
                    <td className="px-5 py-3">{r.description}</td>
                    <td className="px-5 py-3 text-xs text-slate-600">{r.category || "—"}</td>
                    <td className="px-5 py-3 text-xs">{r.payee || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums text-emerald-700">
                      {r.amount_in ? formatCurrency(r.amount_in) : "—"}
                    </td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums text-red-700">
                      {r.amount_out ? formatCurrency(r.amount_out) : "—"}
                    </td>
                    <td className="px-5 py-3 text-right font-mono-alt tabular-nums font-semibold">
                      {formatCurrency(r.running_balance)}
                    </td>
                    <td className="px-5 py-3">
                      {r.document_url
                        ? <a href={r.document_url} target="_blank" rel="noreferrer" className="text-[#1E5CBA]"><FileText size={12} /></a>
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PettyCashAddDialog open={addOpen} onClose={() => setAddOpen(false)} onDone={load} />
      <RecordInspector {...inspector.props} />
    </div>
  );
}

function PettyCashAddDialog({ open, onClose, onDone }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    date: today, description: "", category: "Office Expense", payee: "",
    amount_in: 0, amount_out: 0, notes: "", document_url: "", document_name: "",
  });
  const fileRef = useRef(null);
  const { upload, busy } = useUpload();

  useEffect(() => {
    if (open) {
      setForm({
        date: today, description: "", category: "Office Expense", payee: "",
        amount_in: 0, amount_out: 0, notes: "", document_url: "", document_name: "",
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onFile = async (file) => {
    if (!file) return;
    try {
      const r = await upload(file);
      setForm((f) => ({ ...f, document_url: r.url, document_name: r.name }));
      toast.success("Receipt attached");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submit = async () => {
    if (!form.description.trim()) return toast.error("Description is required");
    if ((form.amount_out || 0) > 20 && !form.document_url) {
      return toast.error("Receipt is required for petty cash payments above $20");
    }
    try {
      await api.post("/finance/petty-cash", form);
      toast.success("Entry added");
      onClose();
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const overTwenty = (form.amount_out || 0) > 20;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader><DialogTitle>Petty cash entry</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
          <div>
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Office Expense", "Fuel", "Printing", "Travel", "Replenishment", "Misc"].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Description *</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pc-description" />
          </div>
          <div><Label>Payee</Label><Input value={form.payee} onChange={(e) => setForm({ ...form, payee: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-emerald-700">Amount In</Label>
              <Input type="number" step="0.01" value={form.amount_in} onChange={(e) => setForm({ ...form, amount_in: parseFloat(e.target.value || 0) })} />
            </div>
            <div>
              <Label className="text-red-700">Amount Out</Label>
              <Input type="number" step="0.01" value={form.amount_out} onChange={(e) => setForm({ ...form, amount_out: parseFloat(e.target.value || 0) })} data-testid="pc-amount-out" />
            </div>
          </div>
          <div className="col-span-2 rounded-md border border-dashed border-slate-300 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <Label className="flex items-center gap-1">
                  <Paperclip size={12} /> Receipt {overTwenty && <span className="text-red-600">*</span>}
                </Label>
                <div className="text-[11px] text-slate-500">Required for outflows above $20.</div>
                {form.document_name && (
                  <div className="mt-1 text-xs text-slate-700">
                    <FileText size={12} className="inline" /> {form.document_name}
                  </div>
                )}
              </div>
              <Input ref={fileRef} type="file" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
              <Button
                type="button" variant="outline" size="sm" disabled={busy}
                onClick={() => fileRef.current?.click()} data-testid="pc-doc-attach"
              >
                {busy ? "Uploading…" : form.document_url ? "Replace" : "Attach"}
              </Button>
            </div>
          </div>
          <div className="col-span-2">
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} className="btn-primary-blue" data-testid="pc-save-btn">Save entry</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
